import { useState } from 'react'
import { Check, DoorOpen, Hand, UserX, X } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { admitToMeeting, denyFromMeeting, removeParticipant } from './api'
import { waitedLabel } from './vocab'
import type { MeetingParticipant } from './types'

/**
 * The door, as the chair sees it.
 *
 * ## Admit everyone is one button and one request
 *
 * Forty people behind a public link is the normal case for a rally briefing, and admitting
 * them one at a time means forty requests, a queue that visibly drains while new people
 * arrive, and a chair who is doing clerical work instead of chairing. The server admits
 * everybody waiting at the moment the button is pressed; somebody who knocks during that
 * write stays in the queue, which is right — they were not in the list the chair agreed to.
 *
 * ## A name here is not an identity
 *
 * Guests type their own name and nobody verifies it. The list says so in as many words,
 * because a chair glancing at "Jane Mwangi" and admitting on sight is exactly the mistake
 * this screen can invite.
 */
export function LobbyPanel({
  meetingId, waiting, inRoom, onChanged,
}: {
  meetingId: string
  waiting: MeetingParticipant[]
  inRoom: MeetingParticipant[]
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
     Hands first, in the order they went up; everybody else in roster order behind them.
     This list is where a chair acts, so it is sorted by what the chair has to act on — and
     the person who has been waiting longest is at the top, which is the only reason the
     server keeps a timestamp rather than a flag.
  */
  const seated = [...inRoom].sort((a, b) => {
    if (Boolean(a.handRaisedAt) !== Boolean(b.handRaisedAt)) return a.handRaisedAt ? -1 : 1
    if (a.handRaisedAt && b.handRaisedAt) return a.handRaisedAt.localeCompare(b.handRaisedAt)
    return 0
  })
  const handCount = inRoom.filter((p) => p.handRaisedAt).length

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onChanged()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Panel>
        <SectionHeader
          title={waiting.length === 0 ? 'Nobody waiting' : `${waiting.length} at the door`}
          description={waiting.length === 0
            ? 'People who open the link appear here until you let them in.'
            : 'Names are typed by whoever is holding the link. Nobody verifies them.'}
          right={waiting.length > 0 ? (
            <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm" disabled={busy}
              onClick={() => run(() => admitToMeeting(meetingId, { all: true }))}>
              <Check className="size-4" /> Admit everyone ({waiting.length})
            </button>
          ) : undefined}
        />

        <ErrorStrip message={error} />

        {waiting.length > 0 && (
          <ul className="divide-y divide-border">
            {waiting.map((person) => (
              <li key={person.id} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="inline-flex items-center gap-2 font-medium truncate">
                    <DoorOpen className="size-4 text-amber-600 shrink-0" />
                    {person.name ?? 'Someone'}
                  </span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {person.guest ? 'Guest, unverified name' : 'Has an account'}
                    {' · waiting '}{waitedLabel(person.knockedAt)}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <button type="button" className="erp-btn erp-btn-ghost h-8 text-erp-sm" disabled={busy}
                    onClick={() => run(() => denyFromMeeting(meetingId, [person.id]))}>
                    <X className="size-4" /> Refuse
                  </button>
                  <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-sm" disabled={busy}
                    onClick={() => run(() =>
                      admitToMeeting(meetingId, { participantIds: [person.id] }))}>
                    <Check className="size-4" /> Admit
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel>
        <SectionHeader
          title="In the meeting"
          description={handCount === 0
            ? "Reported by the media server, not by anybody's browser."
            : `${handCount === 1 ? 'One hand is' : `${handCount} hands are`} up. `
              + 'Whoever asked first is at the top.'}
        />
        {seated.length === 0 ? (
          <Note tone="info">Nobody has connected yet.</Note>
        ) : (
          <ul className="divide-y divide-border">
            {seated.map((person, i) => (
              <li key={person.id} className="flex items-center justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-medium truncate">
                    {person.handRaisedAt && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full
                                       bg-amber-500/15 px-1.5 py-0.5 text-erp-xs text-amber-700">
                        <Hand className="size-3" /> {i + 1}
                      </span>
                    )}
                    <span className="truncate">{person.name ?? 'Someone'}</span>
                  </span>
                  <span className="block text-erp-xs text-muted-foreground">
                    {person.roleCode === 'HOST' ? 'Chair' : person.guest ? 'Guest' : 'Colleague'}
                    {person.handRaisedAt
                      && ` · asking to speak, ${waitedLabel(person.handRaisedAt)}`}
                  </span>
                </span>
                {person.roleCode !== 'HOST' && (
                  <RemoveButton busy={busy} name={person.name ?? 'this person'}
                    onConfirm={(reason) =>
                      run(() => removeParticipant(meetingId, person.id, reason))} />
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

/**
 * Removing somebody asks for the reason, because the server requires one and because the
 * person is entitled to it. The field is not a formality: it is what the audit entry carries
 * and what anybody reviewing the meeting afterwards reads.
 */
function RemoveButton({
  busy, name, onConfirm,
}: { busy: boolean; name: string; onConfirm: (reason: string) => void }) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')

  if (!asking) {
    return (
      <button type="button" className="erp-btn erp-btn-ghost h-8 text-erp-sm text-destructive"
        disabled={busy} onClick={() => setAsking(true)}>
        <UserX className="size-4" /> Remove
      </button>
    )
  }
  return (
    <span className="flex shrink-0 items-center gap-2">
      <input className="erp-input h-8 w-56 text-erp-sm" autoFocus value={reason} maxLength={500}
        placeholder={`Why ${name} is being removed`}
        onChange={(e) => setReason(e.target.value)} />
      <button type="button" className="erp-btn erp-btn-ghost h-8 text-erp-sm"
        onClick={() => { setAsking(false); setReason('') }}>
        Cancel
      </button>
      <button type="button" className="erp-btn erp-btn-primary h-8 text-erp-sm"
        disabled={busy || reason.trim().length < 3}
        onClick={() => { onConfirm(reason.trim()); setAsking(false); setReason('') }}>
        Remove
      </button>
    </span>
  )
}
